import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, X, ArchiveRestore, Loader } from 'lucide-react';
import { useRecoveryLogic } from './hooks/useRecoveryLogic';
import { RouteParams } from '../types';
import { Button } from '../../../components/ui/button';
import { TableCell, TableRow } from '../../../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

const RecoveryFileListItem: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false);
  const { folderId } = useParams<RouteParams>();
  const isInRecentsView = folderId === 'recents';
  const {
    firstRecord,
    isRecovering,
    progress,
    hasMultiple,
    recentRecoverableFiles,
    hasRecoverableFiles,
    handleRecover,
    handleDismiss,
    getTimeAgo,
  } = useRecoveryLogic();

  const handleRecoverClick = () => {
    if (firstRecord) {
      handleRecover(firstRecord);
    }
  };

  const handleDismissClick = (e: React.MouseEvent, fileId: string) => {
    e.stopPropagation();
    handleDismiss(fileId);
  };

  // Don't render if no recent recoverable files
  if (!hasRecoverableFiles) {
    return null;
  }

  return (
    <TableRow
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleRecoverClick}
      className={`
        group relative transition-colors cursor-pointer
        bg-white dark:bg-transparent
        ${isHovered ? '!bg-orange-50/50 dark:!bg-orange-950/10' : 'hover:!bg-muted/20'}
      `}
    >
      {/* Name Column */}
      <TableCell className="h-14 w-[45%] min-w-[250px] pl-4">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded bg-orange-500/10 flex-shrink-0 flex items-center justify-center">
            {isHovered ? (
              <ArchiveRestore className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
            ) : (
              <AlertTriangle className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
            )}
          </div>
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground truncate">
              {hasMultiple
                ? `${recentRecoverableFiles.length} uploads to finalize`
                : 'Finalize upload'}
            </p>
            <p className="text-xs text-muted-foreground">
              {isHovered ? 'Click to finalize' : 'Upload was interrupted'}
            </p>
          </div>
        </div>
      </TableCell>

      {/* Duration Column - Show progress or status */}
      <TableCell className="w-[70px] text-center">
        {isRecovering ? (
          <div className="flex items-center justify-center gap-1.5">
            <Loader className="h-3 w-3 animate-spin text-orange-600 dark:text-orange-400" />
            <span className="text-xs text-muted-foreground">
              {Math.round(progress)}%
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>

      {/* Folder Column - Only show in recents view */}
      {isInRecentsView && (
        <TableCell className="hidden sm:table-cell w-[100px] text-center">
          <span className="text-xs text-muted-foreground">—</span>
        </TableCell>
      )}

      {/* Creator Column - Show dash */}
      <TableCell className="hidden md:table-cell w-[80px] text-center">
        <span className="text-xs text-muted-foreground">—</span>
      </TableCell>

      {/* Date Column - Show time ago */}
      <TableCell className="w-[60px] text-center">
        <span className="text-xs text-muted-foreground">
          {firstRecord && getTimeAgo(firstRecord.startedAt)}
        </span>
      </TableCell>

      {/* Action Menu Column with Dismiss Button */}
      <TableCell className="w-[44px]">
        <div className="flex justify-end">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 hover:bg-muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (firstRecord) {
                      handleDismissClick(e, firstRecord.fileId);
                    }
                  }}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Dismiss</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default RecoveryFileListItem;
